import { getFreshSupabaseToken } from './tokenRefresh';

const getApiUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    return 'https://onepurposelogistics-api.onrender.com';
  }
  return 'http://localhost:5000';
};

export const API_BASE_URL = getApiUrl();
export const API_URL = `${API_BASE_URL}/api`;
export const SERVER_ORIGIN = API_BASE_URL;

/**
 * Turn a stored file URL into an absolute URL the browser can load.
 * Uploads are stored as `/uploads/<file>` but are served by the BACKEND
 * origin — so `<img>` / `<a>` tags need the absolute URL.
 */
export const resolveUploadUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//.test(url) || /^data:/.test(url)) return url;
  if (url.startsWith('/uploads/')) return API_BASE_URL + url;
  return url;
};

// Global fetch wrapper (Option A): every fetch() to our own server
// automatically gets the app token AND the auto-refreshed RLS login card.
// Never overwrites existing headers; never touches other origins; skips
// public endpoints. Attaching the card even when a caller sent its own
// Authorization header is required by strict-mode RLS (not a back door —
// the server still rejects requests without a valid card).

const PUBLIC_PATHS = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
  '/api/auth/magic-link',
  '/api/auth/refresh-token',
  '/api/users/set-password',
  '/api/clients/register',
  '/api/subcontractors/register',
  '/api/subcontractors/verify-email',
  '/api/site-inspections/public/',
  '/api/health'
];

const isOwnServer = (url) => {
  if (!url) return false;
  if (url.startsWith('/')) return true;
  try {
    const target = new URL(url, window.location.origin);
    return target.origin === API_BASE_URL || target.origin === window.location.origin;
  } catch {
    return false;
  }
};

const toPath = (url) => {
  if (url.startsWith('/')) return url;
  try { return new URL(url, window.location.origin).pathname; } catch { return url; }
};

const isPublicPath = (url) => PUBLIC_PATHS.some((p) => toPath(url).startsWith(p));

const hasHeader = (headers, name) => {
  if (!headers) return false;
  const lower = name.toLowerCase();
  if (headers instanceof Headers) return headers.has(name);
  if (Array.isArray(headers)) return headers.some(([k]) => String(k).toLowerCase() === lower);
  return Object.keys(headers).some((k) => k.toLowerCase() === lower);
};

const nativeFetch = window.fetch.bind(window);

window.fetch = async (input, init = {}) => {
    let url = typeof input === 'string' ? input : (input && (input.url || input.href)) || '';
    let headers = init.headers;
    if (url.startsWith('/') && !url.startsWith(API_BASE_URL)) {
        const prefix = (url.startsWith('/api/') || url.startsWith('/uploads/')) ? '' : '/api';
        url = `${API_BASE_URL}${prefix}${url}`;
    }

  if (isOwnServer(url) && !isPublicPath(url)) {
    const token = localStorage.getItem('token');
    const supabaseToken = await getFreshSupabaseToken();
    if (token || supabaseToken) {
      const hasAuth = hasHeader(headers, 'Authorization');
      const hasCard = hasHeader(headers, 'x-supabase-token');
      let h = headers;

      if (h instanceof Headers) {
        if (token && !hasAuth) h.append('Authorization', `Bearer ${token}`);
        if (supabaseToken && !hasCard) h.append('x-supabase-token', supabaseToken);
      } else if (Array.isArray(h)) {
        h = [...h];
        if (token && !hasAuth) h.push(['Authorization', `Bearer ${token}`]);
        if (supabaseToken && !hasCard) h.push(['x-supabase-token', supabaseToken]);
      } else if (h && typeof h === 'object') {
        h = { ...h };
        if (token && !hasAuth) h.Authorization = `Bearer ${token}`;
        if (supabaseToken && !hasCard) h['x-supabase-token'] = supabaseToken;
      } else {
        h = {};
        if (token) h.Authorization = `Bearer ${token}`;
        if (supabaseToken) h['x-supabase-token'] = supabaseToken;
      }
      init.headers = h;
    }
  }

  // Never serve API responses from the browser's cache. Without explicit
  // cache headers, browsers heuristically cache GET responses and can return
  // STALE data after a write (e.g. saving a tracking link but the loads list
  // still showing "Add Tracking Link" until a hard refresh).
  if (!init || typeof init !== 'object') init = {};
  init.cache = 'no-store';

  return nativeFetch(typeof input === 'string' ? url : input, init);
};

export const authFetch = window.fetch;

export default {
  API_BASE_URL,
  API_URL,
  SERVER_ORIGIN,
  authFetch
};